import { fileNameFromDisposition } from './download.ts';

export class ApiError extends Error {
  readonly status: number;
  readonly url: string;

  /** Machine-readable code from the API, e.g. "room_not_operated". */
  readonly code?: string;

  /** The API's own explanation, when it sent one. Safe to show a user. */
  readonly serverMessage?: string;

  /*
   * Written out longhand rather than as constructor parameter properties, which is the one
   * TypeScript feature Node's strip-only type erasure cannot emit — so the parameter-property
   * form made this module unimportable from `node --test`, and therefore untestable.
   * `packages/ui`'s `expression.ts` carries the same note for the same reason.
   */
  constructor(
    status: number,
    url: string,
    message: string,
    code?: string,
    serverMessage?: string,
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.url = url;
    this.code = code;
    this.serverMessage = serverMessage;
  }
}

/**
 * The response arrived and its body could not be read.
 *
 * <p>
 * <b>A subclass, because it IS an answer from the API — it is not a connection failure, and that
 * distinction is the whole reason the type exists.</b> Before it, anything that was not an
 * `ApiError` fell through `describeApiError`'s first guard to "Check your connection", so a body
 * that would not parse was reported as a network fault. The instance that found it was a
 * <b>successful 200</b>: `GET /api/rating-boards/{id}/standings` on a board nobody had
 * recalculated answered a zero-length body, because ASP.NET's JSON writer skips a null value
 * entirely. An operator was told to check their connection over a request that had worked.
 * </p>
 * <p>
 * That particular body is fixed at the server (see `RatingBoardStandingsDto`). This closes the
 * class, and the class is not theoretical: an unproxied path in a Vite dev server answers
 * `index.html` with a 200, which is the same failure wearing the same wrong sentence — and it is
 * already recorded as a gotcha for exactly one endpoint.
 * </p>
 * <p>
 * It carries the real status, which is the surprising half and belongs in the message: "the server
 * sent a 200 this app could not read" points at the app or the API, where "check your connection"
 * points at the one place the fault is not.
 * </p>
 */
export class UnreadableResponseError extends ApiError {
  constructor(status: number, url: string, detail: string) {
    super(
      status,
      url,
      `The server's response to ${url} could not be read (${status}): ${detail}`,
      'unreadable_response',
    );
    this.name = 'UnreadableResponseError';
  }
}

/** The API's error envelope: { error: { code, message } }. */
async function readErrorBody(
  response: Response,
): Promise<{ code?: string; message?: string }> {
  try {
    const body = (await response.json()) as {
      error?: { code?: string; message?: string };
    };
    return { code: body?.error?.code, message: body?.error?.message };
  } catch {
    // Not every failure carries a JSON body — a proxy error, for instance.
    return {};
  }
}

/** A file the API answered: its bytes and the name the server gave it. */
export interface DownloadedFile {
  blob: Blob;
  fileName: string;
}

export interface ApiClientOptions {
  baseUrl: string;
  /** Supplied by @brush/auth's useAccessToken. */
  getAccessToken: () => Promise<string>;
  /**
   * The role the signed-in person chose to act as, or null for "everything the token carries".
   *
   * Read on every request rather than captured once, because it can change without the client
   * being rebuilt — switching role is not a sign-out. @brush/auth's `readActingRole` reads it
   * straight from session storage, which is what keeps this free of a stale closure.
   *
   * The server treats it as a request to *narrow*: it can only ever remove roles the token
   * already carries, never add one, so sending it is safe and omitting it is simply the
   * un-narrowed behaviour.
   */
  getActingRole?: () => string | null;
}

interface RequestOptions {
  authenticated?: boolean;
  /** A token to send as it stands, in place of the getter's. */
  token?: string;
}

/**
 * Thin typed fetch wrapper. Request/response *shapes* come from the generated
 * OpenAPI schema — this file only handles transport, auth headers, and errors.
 */
export function createHttp({ baseUrl, getAccessToken, getActingRole }: ApiClientOptions) {
  /**
   * Sends a request with the bearer header and the acting role, and answers the response when it is a 2xx. Anything
   * else is raised as an ApiError carrying the server's code and sentence — the one refusal path, shared by a JSON
   * call and a download, so an export's refusal reads exactly as any other request's (EX26).
   */
  async function send(
    path: string,
    init: RequestInit,
    { authenticated = true, token }: RequestOptions,
  ): Promise<{ response: Response; url: string }> {
    const url = `${baseUrl.replace(/\/$/, '')}${path}`;

    // Only alongside a token. An anonymous endpoint has no principal to narrow, and the header
    // would be so much noise on the one request whose value is that it works when little else does.
    const actingRole = authenticated ? (getActingRole?.() ?? null) : null;

    const response = await fetch(url, {
      ...init,
      headers: {
        'Content-Type': 'application/json',
        ...(authenticated ? { Authorization: `Bearer ${token ?? (await getAccessToken())}` } : {}),
        ...(actingRole ? { 'X-Acting-Role': actingRole } : {}),
        ...(init.headers ?? {}),
      },
    });

    if (!response.ok) {
      // Keep the server's explanation: without it every failure looks identical to the
      // caller, and a 403 is indistinguishable from a validation error.
      const { code, message } = await readErrorBody(response);
      throw new ApiError(
        response.status,
        url,
        message ?? `Request failed: ${response.status}`,
        code,
        message,
      );
    }
    return { response, url };
  }

  async function request<TResponse>(
    path: string,
    init: RequestInit = {},
    options: RequestOptions = {},
  ): Promise<TResponse> {
    const { response, url } = await send(path, init, options);
    if (response.status === 204) return undefined as TResponse;

    /*
     * A SUCCESS WHOSE BODY WILL NOT PARSE IS AN API PROBLEM, NEVER A CONNECTION ONE.
     *
     * `response.json()` throws a bare SyntaxError on an empty or non-JSON body, and a SyntaxError
     * is not an ApiError — so it used to reach `describeApiError`'s "Check your connection"
     * fallback and send somebody to look at their network over a request that had already
     * succeeded. Naming it is what lets that sentence stay honest for the case it is actually for,
     * which is `fetch` rejecting before any response exists.
     */
    try {
      return (await response.json()) as TResponse;
    } catch (cause) {
      throw new UnreadableResponseError(
        response.status,
        url,
        cause instanceof Error ? cause.message : String(cause),
      );
    }
  }

  /**
   * An export (docs/specs/export.md EX25): the same request as any other — the bearer header, never a token in the
   * URL — whose 2xx is read as a Blob named by the server's `Content-Disposition` (exposed through CORS, EX23), or
   * `fallbackName` when the header is missing. A refusal is the ApiError `send` raises, so an error body is never
   * saved as a file.
   */
  async function download(path: string, fallbackName: string): Promise<DownloadedFile> {
    const { response } = await send(path, { method: 'GET' }, {});
    const blob = await response.blob();
    return { blob, fileName: fileNameFromDisposition(response.headers.get('Content-Disposition')) ?? fallbackName };
  }

  return {
    download,
    get: <T>(path: string) => request<T>(path, { method: 'GET' }),
    /**
     * A GET that sends no Authorization header and never asks for a token.
     *
     * Only for endpoints the server serves anonymously, which today means `/version`. Sending a
     * token there would be merely pointless; *acquiring* one is the problem. A stale MSAL entry in
     * sessionStorage leaves `acquireTokenSilent` looping in hidden iframes, and the whole point of
     * asking the API which build it is running is to answer that question while something else is
     * broken — so it must not be the first thing to go down with it.
     */
    getAnonymous: <T>(path: string) =>
      request<T>(path, { method: 'GET' }, { authenticated: false }),
    post: <T>(path: string, body?: unknown) =>
      request<T>(path, { method: 'POST', body: JSON.stringify(body ?? {}) }),
    put: <T>(path: string, body?: unknown) =>
      request<T>(path, { method: 'PUT', body: JSON.stringify(body ?? {}) }),
    patch: <T>(path: string, body?: unknown) =>
      request<T>(path, { method: 'PATCH', body: JSON.stringify(body ?? {}) }),
    /**
     * `token` sends that token instead of asking `getAccessToken` — erasure's, which must carry the
     * sign-in the person has just made rather than whatever MSAL has cached (ADR 0012 step 28).
     */
    delete: <T>(path: string, options?: { token?: string }) =>
      request<T>(path, { method: 'DELETE' }, { token: options?.token }),
  };
}

export type Http = ReturnType<typeof createHttp>;
